import axios from 'axios';
import isEmpty from 'lodash.isempty';

import AxiosServerInstanceClass from '../utilities/axiosServerInstance';
import { API_URL_NEW, SSR_API_URL_NEW } from '../utilities/endpoints';
import { NEWS_DATA_INSIGHTS_ID } from '../utilities/constants/newsCategories'
import { prepareSelectParam } from '../utilities/queryParams';

export default {
    getFeaturedResearch: async (token = "", refreshToken = "") => {
        const AxiosInstance = new AxiosServerInstanceClass(token, refreshToken);
        AxiosInstance.req();
        AxiosInstance.res();

        try {
            const fields = prepareSelectParam([
                'id',
                'name',
                'code',
                'short_description',
                'body',
                'image_data',
                'published_at',
                'category_data',
                'industry_data',
                'lang',
            ]);

            const URL = `${SSR_API_URL_NEW}/research?fields=${fields}&filters=is_featured^:1^,is_published^:1&sort=published_at^:desc&limit=6`;

            const response = await AxiosInstance.axiosInstance.get(URL);
            const data = await response.data.data;

            return data;
        } catch (error) {
            throw error;
        }
    },

    getResearchListings: (limit, filters, page) => {
        let paramFields = prepareSelectParam([
            'id',
            'name',
            'short_description',
            'image_data',
            'code',
            'sample_data',
            'published_at',
            'type_id',
            'price',
            'lang',
            'sponsor_logo_id',
            'sponsor_logo_data',
            'category_data',
            'industry_data',
            'location_id',
        ]);
        if (!page) {
            page = 1;
        }
        if (!limit) {
            limit = 6;
        }

        let filterParams;
        if (filters !== '') {
            filterParams = `${filters}^,is_published^:1`;
        } else {
            filterParams = `is_published^:1`;
        }
        let query = `fields=${paramFields}&filters=${filterParams}&limit=${limit}&page=${page}&sort=published_at^:desc`;

        const URL = `${API_URL_NEW}/research?${query}`;
        return new Promise((resolve, reject) => {
            axios
                .get(URL)
                .then(async (res) => {
                    if (res && res.data && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        reject(new Error('getResearchListings: response had no data'));
                    }
                })
                .catch((err) => reject(err));
        });
    },

    getResearchSettingCount: (filters) => {
        const URL = `${API_URL_NEW}/research/setting/count?filters=${filters}^,is_published^:1`;
        return new Promise((resolve, reject) => {
            axios
                .get(URL)
                .then(async (res) => {
                    if (res && res.data && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        reject(new Error('getResearchSettingCount: response had no data'));
                    }
                })
                .catch((err) => reject(err));
        });
    },

    fetchResearchDetails: async (id, token = "", refreshToken = "") => {
        const AxiosInstance = new AxiosServerInstanceClass(token, refreshToken);
        AxiosInstance.req();
        AxiosInstance.res();

        const fields = prepareSelectParam([
            'id',
            'code',
            'name',
            'subtitle',
            'full_description',
            'short_description',
            'price',
            'image_data',
            'sample_data',
            'type_id',
            'category_id',
            'category_data',
            'body',
            'published_at',
            'is_user_purchased',
            'download_policy',
            'lang',
            'sponsor_logo_data',
            'preview_1',
            'preview_2',
            'preview_3',
            'is_published',
            'download_form_type',
            'preview_image_data',
            'table_of_contents',
            'table_of_contents_promoted',
            'promoted_research_id'
        ]);
        
        // preview_1 = text that shows on the research dl/purchase widget
        // preview_2 = a desc of the sponsor for sponsored reports
        // preview_3 = sponsor name

        try {
            const researchUrl = `${SSR_API_URL_NEW}/research/${id}?fields=${fields}`;
            const response = await AxiosInstance.axiosInstance.get(researchUrl);
            const researchData = await response.data.data;

            let obj = {};
            obj['researchData'] = researchData;
            return obj && obj.researchData && obj.researchData.id ? obj : 404;
        } catch (error) {
            return 404;
        }
    },

    downloadReport: (reportId, user_id, email) => {
        const URL = `${API_URL_NEW}/research/${reportId}/download`;
        return new Promise((resolve, reject) => {
            axios
                .post(URL, { user_id, email })
                .then(async (response) => {
                    if (response.data.status === 201) {
                        let reply = {
                            success: true,
                        };
                        const downloadPath = response.data.data.report_path_data;
                        if (!isEmpty(downloadPath) && downloadPath.path) {
                            reply.downloadPath = downloadPath.path;
                        } else if (response?.data?.errors) {
                            reply.errors = response.data.errors;
                        }
                        resolve(reply);
                    } else {
                        const error = response?.data?.errors || 'Access denied to the file';
                        reject(error);
                    }
                })
                .catch((err) => reject(err));
        });
    },

    // research logged out download
    // first download, then subscribe to newsletter
    handleLoggedOutDownload: (userData, id, mailchimpData) => {
        return new Promise((resolve, reject) => {
            const downloadURL = `${API_URL_NEW}/research/${id}/download`;
            const newsletterURL = `${API_URL_NEW}/mailchimp/member`;

            axios
                .post(downloadURL, userData)
                .then(async (response) => {
                    if (response.data.status === 201) {
                        if (userData.sub_to_mailchimp) {
                            await axios.post(newsletterURL, mailchimpData).then((response) => {
                                // ensures we wait for the operation to complete
                                // as the request gets cancelled once the page redirects
                                resolve({success: true});
                            });
                        } else {
                            resolve({success: true});
                        }
                    }
                }).catch((error) => {
                    let result = {
                        success: false,
                        emailComplianceError: false,
                    };

                    // Check for 400 status and email compliance error flag
                    if (error?.response?.status === 400 && error.response.data?.data?.isEmailComplianceError) {
                        result.emailComplianceError = true;
                    }

                    resolve(result);
                })
        });
    },

    getResearchCountriesCount: () => {
        const URL = `${API_URL_NEW}/research/setting/countries-research-count`;
        return new Promise((resolve, reject) => {
            axios
                .get(URL)
                .then(async (response) => {
                    if (response.data.status === 200) {
                        resolve(response.data.data);
                    } else {
                        throw new Error(response);
                    }
                })
                .catch((err) => resolve([]));
        });
    },

    getResearchIndustriesCount: () => {
        const URL = `${API_URL_NEW}/research/setting/industries-research-count`;
        return new Promise((resolve, reject) => {
            axios
                .get(URL)
                .then(async (response) => {
                    if (response.data.status === 200) {
                        resolve(response.data.data);
                    } else {
                        throw new Error(response);
                    }
                })
                .catch((err) => resolve([]));
        });
    },

    getResearchDownloadQuota: (userId) => {
        const url = `${API_URL_NEW}/user/${userId}/research-download-quota`;
        return new Promise((resolve, reject) => {
            axios
                .get(url)
                .then((response) => {
                    const data = response.data.data;
                    if (!isEmpty(data.data)) {
                        // subscription period, 3 = monthly, 4 = yearly
                        const period = data.period;
                        const today = new Date();
                        const start = new Date(data.user_sub_date.start_date);
                        const ends = new Date(data.user_sub_date.end_date);

                        let resetDate = new Date(ends);
                        if (period == 3) {
                            const tempDate = new Date(
                                today.getFullYear(),
                                today.getMonth(),
                                start.getDate(),
                            );
                            if (tempDate > today) {
                                resetDate = tempDate;
                            } else if (tempDate < ends) {
                                resetDate = new Date(
                                    today.getFullYear(),
                                    today.getMonth() + 1,
                                    start.getDate(),
                                );
                            }
                        }

                        let options = { year: 'numeric', month: 'short', day: '2-digit' };
                        resolve(resetDate.toLocaleString('en-US', options));
                    }
                })
                .catch((err) => reject(err));
        });
    },

    getResearchDetails: (researchId, fieldsList) => {
        let fields = [];
        if (!fieldsList) {
            // call default fields if none specified
            fields = prepareSelectParam([
                'id',
                'code',
                'name',
                'full_description',
                'short_description',
                'price',
                'image_data',
                'sample_data',
                'type_id',
                'category_id',
                'category_data',
                'body',
                'published_at',
                'is_user_purchased',
                'download_policy',
                'lang',
                'sponsor_logo_data',
                'preview_1',
                'preview_2',
                'preview_3',
                'is_published',
            ]);
        } else {
            fields = prepareSelectParam(fieldsList);
        }

        const URL = `${API_URL_NEW}/research/${researchId}?fields=${fields}`;

        return new Promise((resolve, reject) => {
            axios
                .get(URL)
                .then(async (res) => {
                    if (res && res.data && res.data.data) {
                        resolve({
                            researchData: res.data.data
                        });
                    } else {
                        reject(new Error('getResearchDetails: response had no data'));
                    }
                })
                .catch((err) => reject(err));
        });
    },

    getRelatedResearch: (id) => {
        const URL = `${API_URL_NEW}/research/${id}/related`;
        return new Promise((resolve, reject) => {
            axios.get(URL)
                .then(async (res) => {
                    if (res && res.data && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        reject(new Error('getRelatedResearch: response had no data'));
                    }
                })
                .catch((err) => reject(err));
        });
    },
};
